import type { CouldNotReason, Lang, ProductCategory, ProductStatus, WorkerLang } from './types';

/**
 * Every fixed piece of wording in the app, in one place, so it can be reviewed together
 * (decision 18: Mike's wife checks the Spanish).
 */

export const ownerStrings = {
  hello: { en: 'Hi, {name}', es: 'Hola, {name}' },
  allGood: { en: 'All in order at home', es: 'Todo en orden en casa' },
  todaysCleaning: { en: "Today's cleaning", es: 'Limpieza del día' },
  estimated: { en: 'estimated', es: 'estimados' },
  tasksCompleted: { en: 'tasks completed', es: 'tareas completadas' },
  tasks: { en: 'tasks', es: 'tareas' },
  task: { en: 'task', es: 'tarea' },
  routines: { en: 'Routines', es: 'Rutinas' },
  routinesSub: { en: 'View and edit routines', es: 'Ver y editar rutinas' },
  products: { en: 'Supplies', es: 'Productos' },
  productsSub: { en: 'Inventory and what is missing', es: 'Ver inventario y faltantes' },
  reports: { en: 'Reports', es: 'Reportes' },
  reportsSub: { en: 'History and daily summary', es: 'Historial y resumen del día' },
  settings: { en: 'Settings', es: 'Ajustes' },
  settingsSub: { en: 'House, hours, notifications', es: 'Casa, horarios, notificaciones' },
  tabHome: { en: 'Home', es: 'Inicio' },
  tabRoutines: { en: 'Routines', es: 'Rutinas' },
  tabProducts: { en: 'Supplies', es: 'Productos' },
  tabReports: { en: 'Reports', es: 'Reportes' },
  tabMore: { en: 'More', es: 'Más' },
  viewPdf: { en: 'View list as PDF', es: 'Ver lista en PDF' },
  dayOff: { en: 'No cleaning scheduled this day.', es: 'No hay limpieza este día.' },
  toRestock: { en: '{n} supplies to restock', es: '{n} productos por resurtir' },
  toRestockOne: { en: '1 supply to restock', es: '1 producto por resurtir' },
  seeFullList: { en: 'See full list', es: 'Ver lista completa' },
  showLess: { en: 'Show less', es: 'Ver menos' },
  byCategory: { en: 'Supplies by category', es: 'Productos por categoría' },
  allCategories: { en: 'All', es: 'Todos' },
  todaysReport: { en: "Today's report", es: 'Reporte de hoy' },
  estimatedTime: { en: 'estimated time', es: 'tiempo estimado' },
  pending: { en: 'pending', es: 'pendientes' },
  pendingTasks: { en: 'Pending tasks', es: 'Tareas pendientes' },
  couldNotDo: { en: "Couldn't do", es: 'No se pudo hacer' },
  notYet: { en: 'Not checked yet', es: 'Sin marcar todavía' },
  productsToRestock: { en: 'Supplies to restock', es: 'Productos por resurtir' },
  exportPdf: { en: 'Export to PDF', es: 'Exportar a PDF' },
  history: { en: 'History', es: 'Historial' },
  checkedAt: { en: 'Checked at {time}', es: 'Marcada a las {time}' },
  checkedBy: { en: 'by {name}', es: 'por {name}' },
  inProgress: { en: 'In progress', es: 'En curso' },
  instructions: { en: 'Instructions', es: 'Instrucciones' },
  minutes: { en: 'Minutes', es: 'Minutos' },
  days: { en: 'Days', es: 'Días' },
  noteTranslated: { en: 'Translated automatically in the real app; original shown here.', es: 'En la app real se traduce automáticamente; aquí se ve el original.' },
  taskName: { en: 'Task', es: 'Tarea' },
  staffSees: { en: 'What staff see (translated automatically)', es: 'Lo que ve el personal (traducido automáticamente)' },
  staffSeesSame: { en: 'What staff see', es: 'Lo que ve el personal' },
  translationPending: {
    en: 'In the real app the Spanish is created automatically when you save. Not connected in this prototype.',
    es: 'En la app real, la traducción se crea automáticamente al guardar. No está conectada en este prototipo.',
  },
  save: { en: 'Save', es: 'Guardar' },
  saved: { en: 'Saved on this phone only', es: 'Guardado solo en este teléfono' },
  noInstructions: { en: 'No instructions yet. Tap to add.', es: 'Sin instrucciones. Toca para agregar.' },
  markDone: { en: 'Mark as done', es: 'Marcar como terminado' },
  markUndone: { en: 'Uncheck', es: 'Desmarcar' },
  moreIdeasRoom: { en: 'More ideas for this room', es: 'Más ideas para este cuarto' },
  taskIdeas: { en: 'Ideas (tap one to use it)', es: 'Ideas (toca una para usarla)' },
  updateReady: { en: 'A new version of Dejo Dicho is ready.', es: 'Hay una versión nueva de Dejo Dicho.' },
  updateNow: { en: 'Update', es: 'Actualizar' },
  dayOffGarden: { en: 'No garden work this day.', es: 'No hay trabajo de jardín este día.' },
  addArea: { en: 'Add area', es: 'Agregar área' },
  belongsTo: { en: 'Belongs to', es: 'Pertenece a' },
  areaLabel: { en: 'Area', es: 'Área' },
  workerLanguage: { en: 'Their language', es: 'Su idioma' },
  chooseLanguage: { en: 'Choose a language', es: 'Elige un idioma' },
  errorReference: { en: 'Reference: {ref}', es: 'Referencia: {ref}' },
  getHelp: { en: 'Get help', es: 'Pedir ayuda' },
  somethingWrong: { en: 'Something went wrong on this screen.', es: 'Algo salió mal en esta pantalla.' },
  tryAgain: { en: 'Try again', es: 'Intentar de nuevo' },
  helpSubject: { en: 'Help with Dejo Dicho ({ref})', es: 'Ayuda con Dejo Dicho ({ref})' },
  helpBody: { en: 'Tell us what happened:', es: 'Cuéntanos qué pasó:' },
  gardenToday: { en: 'Garden today', es: 'Jardín del día' },
  moveToRole: { en: 'Change to {role}', es: 'Cambiar a {role}' },
  staffRoleLabel: { en: 'Their work', es: 'Su trabajo' },
  gardenSetupTitle: { en: 'The garden', es: 'El jardín' },
  gardenSetupSub: {
    en: "The gardener's days and hours, and the areas they look after. They see only these, and you get the garden's own report. You can change everything later.",
    es: 'Los días y el horario del jardinero, y las áreas que cuida. Solo verá esto, y tú recibes un reporte aparte del jardín. Puedes cambiar todo después.',
  },
  gardenerName: { en: "Gardener's first name", es: 'Nombre del jardinero' },
  gardenAreas: { en: 'Which areas does the gardener look after?', es: '¿Qué áreas cuida el jardinero?' },
  saveGarden: { en: 'Save the garden', es: 'Guardar el jardín' },
  gardenDaysHours: { en: 'Garden days and hours', es: 'Días y horario del jardín' },
  notToday: { en: 'Not today', es: 'No toca hoy' },
  notOnDay: { en: 'Not on {day}', es: 'No toca el {day}' },
  roomTodayHint: {
    en: 'Today is {day}. Today\'s tasks have a box to check; the grey ones are for other days.',
    es: 'Hoy es {day}. Las tareas de hoy tienen casilla para marcar; las grises son de otros días.',
  },
  roomDayHint: { en: 'Showing {day}. The grey tasks are for other days.', es: 'Viendo el {day}. Las tareas grises son de otros días.' },
  ownerCanCheck: { en: 'You can check tasks off yourself too.', es: 'También puedes marcar tareas tú misma.' },
  setStatus: { en: 'Set status', es: 'Cambiar estado' },
  changedBy: { en: 'Changed by {name}, {when}', es: 'Cambiado por {name}, {when}' },
  house: { en: 'House', es: 'Casa' },
  houseName: { en: 'House name', es: 'Nombre de la casa' },
  staff: { en: 'Staff', es: 'Personal' },
  sendLink: { en: 'Send link', es: 'Enviar enlace' },
  openStaffLink: { en: 'Open the staff link', es: 'Abrir el enlace del personal' },
  linkActive: { en: 'Link active', es: 'Enlace activo' },
  workDaysHours: { en: 'Work days and hours', es: 'Días y horario de trabajo' },
  language: { en: 'Language', es: 'Idioma' },
  howToAddress: { en: 'How the app addresses staff in Spanish', es: 'Cómo le habla la app al personal' },
  tuLabel: { en: '"Tú" (informal)', es: '"Tú" (de confianza)' },
  ustedLabel: { en: '"Usted" (formal)', es: '"Usted" (formal)' },
  notifications: { en: 'Notifications', es: 'Notificaciones' },
  notificationsSub: { en: 'Daily report, supplies running out', es: 'Reporte diario, productos por terminarse' },
  reportByEmail: { en: 'Email me the daily report', es: 'Enviarme el reporte diario por correo' },
  reportByEmailSub: { en: 'When the day closes. Phone notifications come later.', es: 'Cuando se cierra el día. Las notificaciones en el teléfono llegan después.' },
  secondOwner: { en: 'Another person in the home', es: 'Otra persona de la casa' },
  secondOwnerSub: { en: 'Invite someone to manage the house with you', es: 'Invita a alguien a administrar la casa contigo' },
  translations: { en: 'Translations this month', es: 'Traducciones este mes' },
  translationsSub: { en: '{used} of {limit} used. Resets on the 1st.', es: '{used} de {limit} usadas. Se renueva el día 1.' },
  subscription: { en: 'Subscription', es: 'Suscripción' },
  subscriptionSub: { en: 'Free trial', es: 'Prueba gratis' },
  deleteAccount: { en: 'Delete account', es: 'Eliminar cuenta' },
  resetDemo: { en: 'Reset the demo', es: 'Reiniciar la demostración' },
  resetDemoSub: { en: 'Puts the sample house back the way it started', es: 'Regresa la casa de ejemplo a como empezó' },
  prototypeNote: {
    en: 'Prototype: nothing is sent to a server. Changes stay on this phone.',
    es: 'Prototipo: nada se envía a un servidor. Los cambios se quedan en este teléfono.',
  },
  notInPrototype: { en: 'Not in the prototype yet', es: 'Todavía no está en el prototipo' },
  checklistTitle: { en: 'Tasks of the day', es: 'Tareas del día' },
  notes: { en: 'Notes', es: 'Notas' },
  print: { en: 'Print or save as PDF', es: 'Imprimir o guardar como PDF' },
  back: { en: 'Back', es: 'Atrás' },
  room: { en: 'Room', es: 'Cuarto' },
  workDaysValue: { en: 'Mon to Sat, 9:00 to 15:00', es: 'Lun a sáb, 9:00 a 15:00' },
  reportEndNote: {
    en: 'The report is final when {name} finishes or at {time}. Unchecked tasks then count as not reached.',
    es: 'El reporte queda listo cuando {name} termina o a las {time}. Lo que no se marcó cuenta como no alcanzado.',
  },
  // Sign-in
  welcome: { en: 'Welcome', es: 'Te damos la bienvenida' },
  emailLabel: { en: 'Your email', es: 'Tu correo electrónico' },
  emailPlaceholder: { en: 'name@email.com', es: 'nombre@correo.com' },
  otherDays: { en: '{n} tasks on other days', es: '{n} tareas en otros días' },
  otherRooms: { en: 'Other rooms', es: 'Otros cuartos' },
  sendCode: { en: 'Send me a code', es: 'Envíame un código' },
  noPassword: { en: 'No password. We email you a 6-digit code.', es: 'Sin contraseña. Te enviamos un código de 6 dígitos.' },
  codeSent: { en: 'We sent a 6-digit code to {email}.', es: 'Enviamos un código de 6 dígitos a {email}.' },
  codeLabel: { en: 'Code', es: 'Código' },
  continue: { en: 'Continue', es: 'Continuar' },
  otherEmail: { en: 'Use a different email', es: 'Usar otro correo' },
  devCode: { en: 'Testing: emails are not sent yet. Your code is {code}.', es: 'Prueba: todavía no se envían correos. Tu código es {code}.' },
  errWrongCode: { en: 'That code is not right. Try again.', es: 'Ese código no es correcto. Intenta de nuevo.' },
  errCodeExpired: { en: 'That code expired. Ask for a new one.', es: 'El código venció. Pide uno nuevo.' },
  errTooMany: { en: 'Too many codes requested. Try again in an hour.', es: 'Pediste demasiados códigos. Intenta en una hora.' },
  errOffline: { en: "Can't reach the server. Check the connection.", es: 'No hay conexión con el servidor. Revisa la conexión.' },
  errGeneric: { en: 'Something went wrong. Try again.', es: 'Algo salió mal. Intenta de nuevo.' },
  errEmail: { en: 'Check the email address.', es: 'Revisa el correo.' },
  errTooManyRequests: { en: 'Too many attempts from this connection. Wait an hour or switch to mobile data.', es: 'Demasiados intentos desde esta conexión. Espera una hora o cambia a datos móviles.' },
  errEmailCeiling: { en: 'We cannot send more emails to this home this month. Write to support@dejodicho.com.', es: 'Este mes ya no podemos enviar más correos a esta casa. Escríbenos a support@dejodicho.com.' },
  // Moving a day, one time (Mike's wife, 2026-10-22)
  moveDay: { en: 'Move to another day', es: 'Cambiar de día' },
  todayShort: { en: 'Today', es: 'Hoy' },
  moveDaySub: { en: 'For this time only. The weekly routine stays the same.', es: 'Solo por esta vez. La rutina de cada semana no cambia.' },
  moveFrom: { en: 'Day to move', es: 'Día que se cambia' },
  moveTo: { en: 'New day', es: 'Nuevo día' },
  moveSummary: { en: '{n} unfinished tasks from {from} move to {to}.', es: '{n} tareas pendientes del {from} pasan al {to}.' },
  moveSummaryOne: { en: '1 unfinished task from {from} moves to {to}.', es: '1 tarea pendiente del {from} pasa al {to}.' },
  moveConfirm: { en: 'Move the day', es: 'Cambiar el día' },
  movesTitle: { en: 'Day changes', es: 'Cambios de día' },
  moveLine: { en: '{from} → {to} · {n} tasks', es: '{from} → {to} · {n} tareas' },
  moveUndo: { en: 'Undo', es: 'Deshacer' },
  moveUndoConfirm: { en: 'Put these tasks back on their own day?', es: '¿Regresar estas tareas a su día?' },
  movedAway: { en: "Today's unfinished tasks were moved to {to}.", es: 'Las tareas pendientes de hoy pasaron al {to}.' },
  movedHere: { en: 'Includes tasks moved from {from}.', es: 'Incluye tareas del {from}.' },
  moveNothing: { en: 'That day has no unfinished tasks.', es: 'Ese día no tiene tareas pendientes.' },
  moveAlready: { en: 'That day already has a change. Undo it first.', es: 'Ese día ya tiene un cambio. Deshazlo primero.' },
  moveNoDays: { en: 'No coming day has tasks to move.', es: 'Ningún día próximo tiene tareas para cambiar.' },
  // Help, devices, and your data (company standard, sections 6 and 7)
  help: { en: 'Help', es: 'Ayuda' },
  helpSub: { en: 'Answers, and how to reach us', es: 'Respuestas y cómo escribirnos' },
  helpSearch: { en: 'Search, or paste the message you saw', es: 'Busca, o pega el mensaje que viste' },
  helpNoMatch: { en: 'Nothing found. Write to us and we will help.', es: 'No encontramos nada. Escríbenos y te ayudamos.' },
  helpWrite: { en: 'Write to us', es: 'Escríbenos' },
  helpReply: { en: 'We reply within a day, in Spanish or English.', es: 'Respondemos en un día, en español o en inglés.' },
  appVersion: { en: 'Version {v}', es: 'Versión {v}' },
  devices: { en: 'Signed-in devices', es: 'Dispositivos con sesión iniciada' },
  devicesSub: { en: 'Sign out a phone or computer you no longer use', es: 'Cierra la sesión de un teléfono o computadora que ya no usas' },
  thisDevice: { en: 'This device', es: 'Este dispositivo' },
  unknownDevice: { en: 'Phone or computer', es: 'Teléfono o computadora' },
  lastUsed: { en: 'Last used {when}', es: 'Último uso: {when}' },
  signOutDevice: { en: 'Sign out', es: 'Cerrar sesión' },
  signOutDeviceConfirm: { en: 'Sign out this device? It will need a new code to come back.', es: '¿Cerrar la sesión en este dispositivo? Necesitará un código nuevo para volver a entrar.' },
  downloadData: { en: 'Download my data', es: 'Descargar mis datos' },
  downloadDataSub: { en: 'A file with everything we keep about you and your home', es: 'Un archivo con todo lo que guardamos de ti y de tu casa' },
  viewingAs: { en: 'Support view: read-only, ends at {time}', es: 'Vista de soporte: solo lectura, termina a las {time}' },
  readOnlyView: { en: 'This is a read-only view. Nothing can be changed.', es: 'Es una vista de solo lectura. No se puede cambiar nada.' },
  // First-run setup
  stepOf: { en: 'Step {n} of {total}', es: 'Paso {n} de {total}' },
  setupHouse: { en: 'Your house', es: 'Tu casa' },
  yourName: { en: 'Your name', es: 'Tu nombre' },
  country: { en: 'Country', es: 'País' },
  countryUS: { en: 'United States', es: 'Estados Unidos' },
  countryMX: { en: 'Mexico', es: 'México' },
  setupRooms: { en: 'Which rooms does your house have?', es: '¿Qué cuartos tiene tu casa?' },
  setupTasks: { en: 'Suggested tasks', es: 'Tareas sugeridas' },
  setupTasksSub: { en: 'Untick what you do not need. You can change everything later.', es: 'Quita lo que no necesites. Puedes cambiar todo después.' },
  setupDays: { en: 'Work days and hours', es: 'Días y horario de trabajo' },
  startTime: { en: 'Starts', es: 'Entrada' },
  endTime: { en: 'Ends', es: 'Salida' },
  setupStaff: { en: 'Who cleans your house?', es: '¿Quién limpia tu casa?' },
  firstName: { en: 'First name', es: 'Nombre' },
  addLater: { en: 'Optional. You can add this later.', es: 'Opcional. Puedes agregarlo después.' },
  next: { en: 'Next', es: 'Siguiente' },
  createHouse: { en: 'Create my house', es: 'Crear mi casa' },
  freqDaily: { en: 'Every work day', es: 'Cada día de trabajo' },
  freqTwice: { en: 'Twice a week', es: 'Dos veces por semana' },
  freqWeekly: { en: 'Once a week', es: 'Una vez por semana' },
  perWeek: { en: '{n} tasks, {time} a week', es: '{n} tareas, {time} por semana' },
  // Editing
  addRoom: { en: 'Add room', es: 'Agregar cuarto' },
  editRoom: { en: 'Edit room', es: 'Editar cuarto' },
  roomName: { en: 'Room name', es: 'Nombre del cuarto' },
  icon: { en: 'Icon', es: 'Ícono' },
  deleteRoom: { en: 'Delete room', es: 'Eliminar cuarto' },
  confirmDeleteRoom: { en: 'Delete this room and its tasks? Past reports keep them.', es: '¿Eliminar este cuarto y sus tareas? Los reportes anteriores las conservan.' },
  addTask: { en: 'Add task', es: 'Agregar tarea' },
  newTask: { en: 'New task', es: 'Nueva tarea' },
  deleteTask: { en: 'Delete task', es: 'Eliminar tarea' },
  confirmDeleteTask: { en: 'Delete this task? Past reports keep it.', es: '¿Eliminar esta tarea? Los reportes anteriores la conservan.' },
  addProduct: { en: 'Add supply', es: 'Agregar producto' },
  name: { en: 'Name', es: 'Nombre' },
  category: { en: 'Category', es: 'Categoría' },
  deleteProduct: { en: 'Delete supply', es: 'Eliminar producto' },
  edit: { en: 'Edit', es: 'Editar' },
  cancel: { en: 'Cancel', es: 'Cancelar' },
  delete: { en: 'Delete', es: 'Eliminar' },
  waitingTranslation: {
    en: 'Translating. Staff will see the Spanish in a few seconds.',
    es: 'Traduciendo. El personal verá el español en unos segundos.',
  },
  waitingTranslationEn: {
    en: 'Written in Spanish. The English translation arrives in a few seconds.',
    es: 'Escrito en español. La traducción al inglés llega en unos segundos.',
  },
  overLimit: {
    en: "This month's translations are used up, so staff see this in English until the 1st. You can type the Spanish yourself below.",
    es: 'Ya se usaron las traducciones de este mes; el personal lo ve en inglés hasta el día 1. Puedes escribir el español tú misma abajo.',
  },
  spanishForStaff: { en: 'Spanish for staff (you can correct it)', es: 'Español para el personal (puedes corregirlo)' },
  taskNameEs: { en: 'Task name in Spanish', es: 'Nombre de la tarea en español' },
  instructionsEs: { en: 'Instructions in Spanish', es: 'Instrucciones en español' },
  correctedByYou: { en: 'Corrected by you. Automatic translation will not change it.', es: 'Corregido por ti. La traducción automática no lo cambia.' },
  noDaysWarning: { en: 'Pick at least one day, or this task never appears.', es: 'Elige al menos un día, o esta tarea nunca aparece.' },
  // Settings
  signOut: { en: 'Sign out', es: 'Cerrar sesión' },
  signOutAll: { en: 'Sign out on all devices', es: 'Cerrar sesión en todos los dispositivos' },
  signOutAllConfirm: {
    en: 'Sign out on every phone and computer, including this one? You can sign in again with a code sent to your email.',
    es: '¿Cerrar sesión en todos los teléfonos y computadoras, incluido este? Puedes volver a entrar con un código que te llega al correo.',
  },
  addStaff: { en: 'Add staff member', es: 'Agregar personal' },
  copyLink: { en: 'Copy link', es: 'Copiar enlace' },
  showQr: { en: 'Show QR code', es: 'Mostrar código QR' },
  qrHelp: { en: 'On the staff phone, open the camera and point it at this code.', es: 'En el teléfono del personal, abre la cámara y apunta a este código.' },
  linkCopied: { en: 'Link copied', es: 'Enlace copiado' },
  newLink: { en: 'Make a new link', es: 'Crear enlace nuevo' },
  newLinkConfirm: { en: 'The old link will stop working. Continue?', es: 'El enlace anterior dejará de funcionar. ¿Continuar?' },
  turnOffLink: { en: 'Turn off link', es: 'Desactivar enlace' },
  turnOnLink: { en: 'Turn on link', es: 'Activar enlace' },
  linkOff: { en: 'Link turned off', es: 'Enlace desactivado' },
  removeStaff: { en: 'Remove', es: 'Quitar' },
  removeStaffConfirm: { en: 'Remove {name}? Their past checks stay in reports.', es: '¿Quitar a {name}? Sus registros anteriores se quedan en los reportes.' },
  sendLinkTo: { en: 'Send the link to {name}', es: 'Enviar el enlace a {name}' },
  staffPageNote: {
    en: 'The staff page still shows the sample house until the next update.',
    es: 'La página del personal todavía muestra la casa de ejemplo hasta la próxima actualización.',
  },
  historyLater: { en: 'Report history arrives with a later update.', es: 'El historial de reportes llega en una actualización próxima.' },
  ownersLabel: { en: 'Owners', es: 'Dueños' },
  you: { en: 'you', es: 'tú' },
  account: { en: 'Account', es: 'Cuenta' },
  formerStaff: { en: 'Former staff', es: 'Personal anterior' },
  // Reports and history
  reportReady: { en: 'Done for the day', es: 'Día cerrado' },
  notReached: { en: 'Not reached', es: 'No se alcanzó' },
  noHistory: { en: 'Past days appear here once a day closes.', es: 'Los días anteriores aparecen aquí cuando se cierra cada día.' },
  reportOf: { en: 'Report', es: 'Reporte' },
  // Second owner
  inviteEmail: { en: "Their email", es: 'Su correo' },
  invite: { en: 'Send invitation', es: 'Enviar invitación' },
  invitePending: { en: 'Invited: {email}. They sign in with that email to join.', es: 'Invitación enviada a {email}. Entra con ese correo para unirse.' },
  cancelInvite: { en: 'Cancel invitation', es: 'Cancelar invitación' },
  removeOwner: { en: 'Remove from the house', es: 'Quitar de la casa' },
  removeOwnerConfirm: { en: 'Remove {name} from this house? Their account stays, without the house.', es: '¿Quitar a {name} de esta casa? Su cuenta sigue, sin la casa.' },
  ownerLimit: { en: 'A house can have two owners.', es: 'Una casa puede tener dos dueños.' },
  hasHousehold: { en: 'That email already has its own house in the app.', es: 'Ese correo ya tiene su propia casa en la app.' },
  // Account deletion
  deleteAccountConfirm: {
    en: 'Delete your account? If you are the only owner, the whole house is deleted too: routines, reports, and staff links. This cannot be undone.',
    es: '¿Eliminar tu cuenta? Si eres la única persona dueña, también se elimina toda la casa: rutinas, reportes y enlaces del personal. No se puede deshacer.',
  },
  deleteAccountSub: { en: 'Deletes your account and your data', es: 'Elimina tu cuenta y tus datos' },
  // Invited owner's first visit
  welcomeJoin: { en: 'Welcome to {house}', es: 'Te damos la bienvenida a {house}' },
  welcomeJoinSub: { en: 'You were invited to manage this house. What is your name?', es: 'Te invitaron a administrar esta casa. ¿Cómo te llamas?' },
} satisfies Record<string, Record<Lang, string>>;

export type OwnerKey = keyof typeof ownerStrings;

export function t(lang: Lang, key: OwnerKey, vars?: Record<string, string | number>): string {
  return fill(ownerStrings[key][lang], vars);
}

/**
 * The worker page's own words, one table per worker language (Mike, 2026-10-15). Spanish always
 * says "usted" (decision 62). Polish (phase 2) will be written in neutral phrasing that does not
 * need the person's gender.
 */
const staffEs = {
  goodMorning: '¡Buenos días!',
  goodAfternoon: '¡Buenas tardes!',
  goodEvening: '¡Buenas noches!',
  todayYouHave: 'Hoy tiene {n} actividades',
  todayYouHaveOne: 'Hoy tiene 1 actividad',
  nothingToday: 'Hoy no tiene actividades',
  estimatedTime: 'tiempo estimado',
  start: 'Empezar',
  continue: 'Continuar',
  today: 'Hoy',
  products: 'Productos',
  completed: 'completadas',
  pending: 'pendientes',
  week: 'Esta semana',
  tasksShort: 'tareas',
  taskOne: 'tarea',
  approx: 'aprox.',
  instructions: 'Instrucciones',
  markDone: 'Marcar como terminada',
  undo: 'Desmarcar',
  couldNot: 'No se pudo hacer',
  whyNot: '¿Qué pasó?',
  noteOptional: 'Nota (opcional)',
  notePlaceholder: 'Escriba aquí si gusta',
  send: 'Enviar',
  cancel: 'Cancelar',
  marked: 'Terminada',
  markedCouldNot: 'No se pudo hacer',
  timeDisclosure: 'La hora en que marca cada tarea se guarda y aparece en el reporte de la casa.',
  thanks: '¡Gracias por su gran trabajo!',
  allDone: '¡Terminó todo por hoy!',
  productsHelp: 'Toque un producto para avisar cómo está.',
  statusGood: 'Hay suficiente',
  statusLow: 'Queda poco',
  statusOut: 'Se terminó',
  dayOff: 'Día libre',
  upcoming: 'Próximos días',
  offline: 'Sin conexión. Se guarda en el teléfono y se envía después.',
  prototypeNote: 'Prototipo: nada se envía a un servidor.',
  shareMessage: 'Hola {name}, esta es su lista de {brand}:',
  privacyLink: 'Aviso de privacidad',
  privacyUrl: 'https://dejodicho.com/privacidad/',
  checklistTasks: 'Tareas del día',
  checklistSupplies: 'Productos',
  checklistNotes: 'Notas',
  linkGone: 'Este enlace ya no funciona',
  linkGoneHelp: 'Pida un enlace nuevo a la casa.',
  translationPending: '(Traducción pendiente)',
};
export type StaffKey = keyof typeof staffEs;

export const staffStrings: Record<WorkerLang, Record<StaffKey, string>> = {
  es: staffEs,
  en: {
    goodMorning: 'Good morning!',
    goodAfternoon: 'Good afternoon!',
    goodEvening: 'Good evening!',
    todayYouHave: 'You have {n} tasks today',
    todayYouHaveOne: 'You have 1 task today',
    nothingToday: 'No tasks today',
    estimatedTime: 'estimated time',
    start: 'Start',
    continue: 'Continue',
    today: 'Today',
    products: 'Supplies',
    completed: 'done',
    pending: 'pending',
    week: 'This week',
    tasksShort: 'tasks',
    taskOne: 'task',
    approx: 'approx.',
    instructions: 'Instructions',
    markDone: 'Mark as done',
    undo: 'Undo',
    couldNot: "Couldn't do it",
    whyNot: 'What happened?',
    noteOptional: 'Note (optional)',
    notePlaceholder: 'Write here if you like',
    send: 'Send',
    cancel: 'Cancel',
    marked: 'Done',
    markedCouldNot: "Couldn't do it",
    timeDisclosure: "The time you check off each task is saved and shown in the home's report.",
    thanks: 'Thank you for your great work!',
    allDone: 'All done for today!',
    productsHelp: 'Tap a supply to say how it is.',
    statusGood: 'Enough',
    statusLow: 'Running low',
    statusOut: 'Ran out',
    dayOff: 'Day off',
    upcoming: 'Coming days',
    offline: 'No connection. Saved on the phone and sent later.',
    prototypeNote: 'Prototype: nothing is sent to a server.',
    shareMessage: 'Hi {name}, here is your {brand} list:',
    privacyLink: 'Privacy notice',
    privacyUrl: 'https://dejodicho.com/en/privacy/',
    checklistTasks: "Today's tasks",
    checklistSupplies: 'Supplies',
    checklistNotes: 'Notes',
    linkGone: 'This link no longer works',
    linkGoneHelp: 'Ask the home for a new link.',
    translationPending: '(Translation pending)',
  },
};

/** A line of the worker page in the worker's language. */
export function ts(lang: WorkerLang, key: StaffKey, vars?: Record<string, string | number>): string {
  return fill((staffStrings[lang] ?? staffStrings.es)[key], vars);
}

export const reasonLabels: Record<CouldNotReason, Record<Lang, string>> = {
  no_product: { en: 'No product', es: 'No había producto' },
  no_time: { en: 'Ran out of time', es: 'No hubo tiempo' },
  room_busy: { en: 'Room was in use', es: 'Cuarto ocupado' },
  other: { en: 'Other', es: 'Otro' },
};

export const statusLabels: Record<ProductStatus, Record<Lang, string>> = {
  good: { en: 'Enough', es: 'Hay suficiente' },
  low: { en: 'Running low', es: 'Queda poco' },
  out: { en: 'Ran out', es: 'Se terminó' },
};

export const categoryLabels: Record<ProductCategory, Record<Lang, string>> = {
  general: { en: 'General cleaning', es: 'Limpieza general' },
  bathroom: { en: 'Bathroom', es: 'Baño' },
  kitchen: { en: 'Kitchen', es: 'Cocina' },
  floors: { en: 'Floors', es: 'Pisos' },
  disposables: { en: 'Disposables', es: 'Desechables' },
  other: { en: 'Other', es: 'Otros' },
  garden: { en: 'Garden', es: 'Jardín' },
};

/** The two crews, as owners see them: the house (cleaning) and the garden. */
export const crewLabels: Record<'house' | 'garden', Record<Lang, string>> = {
  house: { en: 'House', es: 'Casa' },
  garden: { en: 'Garden', es: 'Jardín' },
};

/** What each crew's person does, for the staff list. */
export const roleLabels: Record<'house' | 'garden', Record<Lang, string>> = {
  house: { en: 'Housekeeping', es: 'Limpieza' },
  garden: { en: 'Gardening', es: 'Jardinería' },
};

function fill(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}
